import { SessionDeck } from '../components/DeckReleases';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, UserPlus, Check, X, Trash2, RotateCcw, Link2, UserRound } from 'lucide-react';
import { api } from '../api/client';
import type { SessionDetail, SessionParticipant, CardCatalog, ParticipantMatch, Animator } from '../types';
import { ParticipantEditor } from '../components/ParticipantEditor';
import { isRecorded } from '../lib/recorded';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';

interface Props {
  sessionId: number;
  onBack: () => void;
}

/** Fan of category colours from today's picks (deduped, max 4). */
function CardFan({ p }: { p: SessionParticipant }) {
  const colors = Array.from(
    new Set(p.picks.map((pk) => pk.category_color).filter((c): c is string => !!c)),
  ).slice(0, 4);
  if (colors.length === 0) {
    return (
      <span className="fds-fan fds-fan--empty" aria-hidden="true">
        <i style={{ background: 'var(--line)' }} />
      </span>
    );
  }
  return (
    <span className="fds-fan" aria-hidden="true">
      {colors.map((c, i) => <i key={i} style={{ background: c }} />)}
    </span>
  );
}

/**
 * Search the Éveil list and pick a person (full name + measure shown, so two
 * "Céline" can be told apart). Used to add someone to the roster and to tie
 * a typed entry to its Éveil record.
 */
function ParticipantSearch({
  onPick, onCancel, autoFocus = true,
}: { onPick: (m: ParticipantMatch) => void; onCancel: () => void; autoFocus?: boolean }) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [results, setResults] = useState<ParticipantMatch[] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(() => {
      api.get<ParticipantMatch[]>(`/api/participants/search?q=${encodeURIComponent(q.trim())}${activeOnly ? '&active=1' : ''}`)
        .then(setResults)
        .catch(() => setResults([]));
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, activeOnly]);

  return (
    <div className="psearch">
      <input
        className="field-input"
        placeholder={t('session.addSearchPlaceholder')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && results && results[0]) onPick(results[0]);
        }}
      />
      <label className="psearch-toggle">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
        {t('session.addOnlyInMeasure')}
      </label>
      <div className="psearch-list">
        {q.trim().length < 2 && <div className="psearch-hint">{t('session.addHelp')}</div>}
        {results && results.length === 0 && <div className="psearch-hint">{t('session.addNoResults')}</div>}
        {results?.map((m) => (
          <button key={m.v2_participant_id} type="button" className="psearch-opt" onClick={() => onPick(m)}>
            <span className="psearch-name">{m.display_name}</span>
            <span className="psearch-meta">
              {m.mandate_label ?? '—'}
              {' · '}
              <span className={`badge ${m.in_measure ? 'badge-accent' : 'badge-muted'}`}>
                {m.in_measure ? t('session.inMeasure') : t('session.notInMeasure')}
              </span>
            </span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
        <button className="btn btn-sm" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

/**
 * La feuille de séance: every participant is a small "hand of cards" —
 * name, a fan of the category colours they touched today, presence and
 * completion state. Opening a hand shows the participant's table
 * (ParticipantEditor); the roster stays one click away. The sheet is open to
 * every intervenant; it says who animates and who recorded each entry.
 */
export function SessionPage({ sessionId, onBack }: Props) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [data, setData] = useState<SessionDetail | null>(null);
  const [catalog, setCatalog] = useState<CardCatalog | null>(null);
  const [selectedSpId, setSelectedSpId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState<number | null>(null);   // sp id being tied to Éveil
  const [animators, setAnimators] = useState<Animator[] | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const refresh = async () => {
    const detail = await api.get<SessionDetail>(`/api/th/sessions/${sessionId}`);
    setData(detail);
  };

  useEffect(() => {
    api.get<CardCatalog>('/api/cards').then(setCatalog);
  }, []);

  // Re-sync the Éveil roster on every open (the server no-ops for closed,
  // deleted, old or non-Éveil séances): people planned or no longer planned
  // since the séance row was created appear/disappear instead of the roster
  // freezing at creation time. Runs in the background — a slow or down Éveil
  // must never block the séance — and only refetches when it changed rows.
  useEffect(() => {
    refresh();
    api.post<{ added: number; removed: number }>(`/api/th/sessions/${sessionId}/hydrate-participants`)
      .then((r) => { if (r.added > 0 || r.removed > 0) refresh(); })
      .catch(() => {});
  }, [sessionId]);

  // A BSP has exactly one participant — go straight to their table.
  useEffect(() => {
    if ((data?.session.session_type === 'bsp' || data?.session.session_type === 'intake_bsp') && data.participants.length === 1 && selectedSpId == null) {
      setSelectedSpId(data.participants[0].id);
    }
  }, [data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setPresence = async (sp: SessionParticipant, presence: 'present' | 'absent') => {
    const next = sp.presence === presence ? 'expected' : presence;
    try {
      await api.patch(`/api/session-participants/${sp.id}/presence`, { presence: next });
    } catch {
      refresh();
      return;
    }
    setData((prev) =>
      prev
        ? { ...prev, participants: prev.participants.map((p) => (p.id === sp.id ? { ...p, presence: next, updated_by_user_id: user?.id ?? p.updated_by_user_id, updated_by_name: user?.name ?? p.updated_by_name } : p)) }
        : prev,
    );
  };

  const addParticipant = async (m: ParticipantMatch) => {
    setError(null);
    try {
      const created = await api.post<SessionParticipant>(`/api/th/sessions/${sessionId}/participants`, {
        v2_participant_id: m.v2_participant_id,
        display_name: m.display_name,
      });
      setAdding(false);
      setData((prev) => {
        if (!prev) return prev;
        const without = prev.participants.filter((p) => p.id !== created.id);
        return {
          ...prev,
          participants: [...without, { ...created, picks: created.picks ?? [] }].sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr')),
          removed_participants: prev.removed_participants.filter((r) => r.id !== created.id),
        };
      });
      setSelectedSpId(created.id);
    } catch (e) {
      setError(String(e));
    }
  };

  const removeParticipant = async (sp: SessionParticipant) => {
    if (!window.confirm(t('session.removeConfirm').replace('{name}', sp.display_name))) return;
    await api.del(`/api/th/sessions/${sessionId}/participants/${sp.id}`);
    if (selectedSpId === sp.id) setSelectedSpId(null);
    refresh();
  };

  const restoreParticipant = async (spId: number) => {
    await api.post(`/api/th/sessions/${sessionId}/participants/${spId}/restore`);
    refresh();
  };

  const linkParticipant = async (spId: number, m: ParticipantMatch) => {
    setError(null);
    try {
      const duplicate = data?.participants.find(p => p.id!==spId && p.v2_participant_id===m.v2_participant_id);
      if (duplicate) {
        if (!window.confirm(`Réunir ces deux entrées pour ${m.display_name} ? L’original et ses observations seront conservés dans les entrées retirées ; les valeurs existantes de la cible sont prioritaires.`)) return;
        await api.post(`/api/quality/merge/${spId}`, { target_id:duplicate.id });
        setLinking(null); setSelectedSpId(duplicate.id); await refresh(); return;
      }
      await api.patch(`/api/session-participants/${spId}/link`, { v2_participant_id: m.v2_participant_id, display_name: m.display_name });
      setLinking(null);
      refresh();
    } catch (e) {
      setError(String(e).includes('409') ? t('session.linkConflict') : String(e));
    }
  };

  const deleteSession = async () => {
    if (!data) return;
    if (!window.confirm(t('session.deleteConfirm'))) return;
    await api.del(`/api/th/sessions/${sessionId}`);
    onBack();
  };

  const restoreSession = async () => {
    await api.post(`/api/th/sessions/${sessionId}/restore`);
    refresh();
  };

  const setDeck = async (deck: 'base' | 'complet' | null) => {
    await api.patch(`/api/th/sessions/${sessionId}`, { deck_used: deck });
    setData((prev) => (prev ? { ...prev, session: { ...prev.session, deck_used: deck } } : prev));
  };

  const openReplace = async () => {
    if (animators === null) setAnimators(await api.get<Animator[]>('/api/th/animators'));
    setReplacing(true);
  };

  const replaceAnimator = async (userId: number) => {
    await api.patch(`/api/th/sessions/${sessionId}`, { animator_user_id: userId });
    setReplacing(false);
    refresh();
  };

  // Field-scoped patches merged onto the LIVE row: an editor's debounced save
  // may fire from a stale closure, so it must never replace the whole
  // participant — only the fields it actually changed. Presence keeps flowing
  // exclusively through setPresence (no patch ever carries it).
  const onParticipantUpdated = (id: number, patch: Partial<SessionParticipant>) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            participants: prev.participants.map((p) => (p.id === id
              ? { ...p, ...patch, updated_by_user_id: user?.id ?? p.updated_by_user_id, updated_by_name: user?.name ?? p.updated_by_name }
              : p)),
          }
        : prev,
    );
  };

  if (!data || !catalog) return <div className="empty-state">{t('common.loading')}</div>;

  const { session, participants, removed_participants } = data;
  const selected = participants.find((p) => p.id === selectedSpId) ?? null;
  // A closed séance is archival (backfilled history): everything counts as recorded.
  const isClosed = session.status === 'closed';
  const recordedFor = (p: SessionParticipant) => isClosed || isRecorded(p, session.scheduled_date);
  const recordedCount = participants.filter(recordedFor).length;
  const isMine = user?.id === session.animator_user_id;

  // The deck actually used: the explicit value, else derived from the picks.
  const anySupplementary = participants.some((p) => p.picks.some((pk) => (pk.pdf_card_number ?? 0) > 40));
  const deckShown = session.deck_used ?? (anySupplementary ? 'complet' : 'base');

  // Next participant in the queue: first unrecorded after the current one
  // (wrapping), else the next row, else none.
  const nextParticipant = (() => {
    if (!selected || participants.length < 2) return null;
    const idx = participants.findIndex((p) => p.id === selected.id);
    for (let step = 1; step < participants.length; step++) {
      const cand = participants[(idx + step) % participants.length];
      if (!recordedFor(cand)) return cand;
    }
    return participants[(idx + 1) % participants.length];
  })();

  const picksSummary = (p: SessionParticipant) => {
    if (p.presence === 'absent') return t('session.presence.absent');
    if (p.picks.length === 0) return t('fds.noCards');
    const names = p.picks.map((pk) => pk.name);
    const shown = names.slice(0, 3);
    const more = names.length - shown.length;
    return `${names.length} ${names.length > 1 ? t('fds.cards') : t('fds.card')} · ${shown.join(' · ')}${more > 0 ? ` +${more}` : ''}`;
  };

  /** "Saisi par X" when the last writer is not the séance's animator. */
  const recordedBy = (p: SessionParticipant) =>
    p.updated_by_user_id != null && p.updated_by_user_id !== session.animator_user_id && p.updated_by_name
      ? p.updated_by_name : null;

  const animatorLine = (
    <span className="attrib-chip" title={t('session.animatedByHelp')}>
      <UserRound size={12} /> {t('session.animatedBy')} <strong>{isMine ? t('session.you') : session.animator_name}</strong>
    </span>
  );

  const deckToggle = (
    <span className="deck-seg" role="radiogroup" aria-label={t('session.deck')} title={t('session.deckHelp')}>
      <span className="deck-seg-label">{t('session.deck')}</span>
      {(['base', 'complet'] as const).map((d) => (
        <button
          key={d}
          type="button"
          role="radio"
          aria-checked={deckShown === d}
          className={`deck-seg-btn ${deckShown === d ? 'deck-seg-btn--active' : ''} ${session.deck_used == null ? 'deck-seg-btn--auto' : ''}`}
          onClick={() => setDeck(session.deck_used === d ? null : d)}
        >
          {t(`session.deck.${d}`)}
        </button>
      ))}
    </span>
  );

  return (
    <div>
      {session.deleted_at && (
        <div className="session-deleted-banner">
          <span>
            <Trash2 size={14} /> {t('session.deleted')}
            {session.deleted_by_name && <> · {t('session.deletedBy')} {session.deleted_by_name}</>}
          </span>
          <button className="btn btn-sm btn-accent" onClick={restoreSession}><RotateCcw size={12} /> {t('session.restore')}</button>
        </div>
      )}

      {selected ? (
        /* One context box: the session line, then the participant line —
           who it is, their presence, and the queue advance. */
        <div className="session-strip session-strip--participant">
          <div className="session-strip-row">
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedSpId(null)}>
              <ArrowLeft size={14} /> {t('fds.backToSheet')}
            </button>
            <span className="session-strip-title">{session.title ?? t('brand.subtitle')}</span>
            <span className="session-strip-meta">
              {formatDate(session.scheduled_date)}
              {session.location && <> · {session.location}</>}
              {session.session_type === 'bsp' && <> · <span className="badge badge-bsp" title={t('upcoming.source.bsp')}>{t('upcoming.badge.bilan')}</span></>}
              {' · '}{animatorLine}
            </span>
            {participants.length > 0 && (
              <span className="session-strip-progress" title={t('session.progressHelp')}>
                <span className="session-progress-count">{recordedCount}/{participants.length}</span>
                <span className="session-progress-label">{t('session.progress')}</span>
              </span>
            )}
            {nextParticipant && (
              <button className="session-strip-next" onClick={() => setSelectedSpId(nextParticipant.id)}>
                {t('editor.nextParticipant')} : {nextParticipant.display_name} <ArrowRight size={13} />
              </button>
            )}
          </div>
          <div className="session-strip-row session-strip-row--who">
            <span className="session-strip-name">
              {selected.display_name}
              <span className="session-strip-name-meta">
                {selected.v2_participant_id ? `Éveil #${selected.v2_participant_id}` : t('editor.adhocLabel')}
                {recordedBy(selected) && <> · <span className="attrib-chip attrib-chip--other">{t('session.recordedBy')} {recordedBy(selected)}</span></>}
              </span>
            </span>
            <span className="presence-seg" role="radiogroup" aria-label={t('editor.presence')}>
              <button
                type="button"
                role="radio"
                aria-checked={selected.presence === 'present'}
                className={`presence-seg-btn presence-seg-btn--present ${selected.presence === 'present' ? 'presence-seg-btn--active' : ''}`}
                onClick={() => setPresence(selected, 'present')}
              >
                <Check size={14} strokeWidth={2.5} /> {t('session.presence.present')}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={selected.presence === 'absent'}
                className={`presence-seg-btn presence-seg-btn--absent ${selected.presence === 'absent' ? 'presence-seg-btn--active' : ''}`}
                onClick={() => setPresence(selected, 'absent')}
              >
                <X size={14} strokeWidth={2.5} /> {t('session.presence.absent')}
              </button>
            </span>
          </div>
        </div>
      ) : (
        <div className="page-header">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={onBack}>
              <ArrowLeft size={14} /> {t('common.back')}
            </button>
            <h1 style={{ margin: '8px 0 0 0' }}>{session.title ?? t('brand.subtitle')}</h1>
            <div className="page-header-meta">
              {formatDate(session.scheduled_date)}
              {session.location && <> · {session.location}</>}
              {session.session_type === 'bsp' && <> · <span className="badge badge-bsp" title={t('upcoming.source.bsp')}>{t('upcoming.badge.bilan')}</span></>}
              {' · '}
              <span className={`badge ${session.status === 'closed' ? 'badge-muted' : 'badge-accent'}`}>
                {session.status === 'closed' ? t('session.status.closed') : t('session.status.open')}
              </span>
              {' · '}{animatorLine}
            </div>
            <div className="session-actions">
              {session.source_journal_id && <span className="badge badge-muted">Reprise du journal #{session.source_journal_id} — présence non déduite</span>}
              {deckToggle}
              <SessionDeck sessionId={sessionId} date={session.scheduled_date} releaseId={session.deck_release_id ?? null} offered={session.base_set_offered ?? null} onSaved={refresh} />
              <button className="btn btn-ghost btn-sm" onClick={openReplace} title={t('session.replaceAnimatorHelp')}>
                <UserRound size={13} /> {t('session.replaceAnimator')}
              </button>
              {!session.deleted_at && (
                <button className="btn btn-ghost btn-sm session-delete" onClick={deleteSession} title={t('session.deleteHelp')}>
                  <Trash2 size={13} /> {t('session.delete')}
                </button>
              )}
            </div>
            {replacing && animators && (
              <div className="replace-box">
                <span className="mat-recap-k">{t('session.replaceAnimatorHelp')}</span>
                <select
                  className="field-input"
                  defaultValue={session.animator_user_id}
                  onChange={(e) => { const id = Number(e.target.value); if (id && id !== session.animator_user_id) replaceAnimator(id); }}
                >
                  {animators.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === user?.id ? ` (${t('session.you')})` : ''}</option>)}
                </select>
                <button className="btn btn-sm" onClick={() => setReplacing(false)}>{t('common.cancel')}</button>
              </div>
            )}
          </div>
          {participants.length > 0 && (
            <div className="session-progress" title={t('session.progressHelp')}>
              <span className="session-progress-count">{recordedCount}/{participants.length}</span>
              <span className="session-progress-label">{t('session.progress')}</span>
              <span className="session-progress-bar">
                <span
                  className="session-progress-fill"
                  style={{ width: `${(recordedCount / participants.length) * 100}%` }}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {error && <div className="badge badge-danger" style={{ display: 'block', padding: '6px 10px', marginBottom: 8 }}>{error}</div>}

      {selected ? (
        <ParticipantEditor
          key={selected.id}
          session={session}
          participant={selected}
          catalog={catalog}
          onUpdated={onParticipantUpdated}
          onSetPresence={(presence) => setPresence(selected, presence)}
          onSyncError={refresh}
        />
      ) : (
        <>
          {participants.length === 0 && !adding && (
            <div className="empty-state">{t('session.empty')}</div>
          )}

          <div className="fds-grid">
            {participants.map((p) => {
              const done = recordedFor(p);
              const by = recordedBy(p);
              return (
                <div
                  key={p.id}
                  className={`fds-card ${p.presence === 'absent' ? 'fds-card--absent' : ''}`}
                >
                  <button className="fds-card-top" onClick={() => setSelectedSpId(p.id)}>
                    <span className="fds-card-name">
                      {p.display_name}
                      {p.v2_participant_id == null && <span className="badge badge-muted fds-adhoc" title={t('session.linkToEveilHelp')}>{t('editor.adhocLabel')}</span>}
                    </span>
                    <CardFan p={p} />
                  </button>
                  <div className="fds-card-picks">{picksSummary(p)}</div>
                  {by && <div className="fds-card-by"><span className="attrib-chip attrib-chip--other">{t('session.recordedBy')} {by}</span></div>}
                  {linking === p.id ? (
                    <div className="fds-card-link">
                      <ParticipantSearch onPick={(m) => linkParticipant(p.id, m)} onCancel={() => setLinking(null)} />
                    </div>
                  ) : (
                    <div className="fds-card-bottom">
                      <span className="fds-presence" role="group" aria-label={`${t('editor.presence')} — ${p.display_name}`}>
                        <button
                          className={`fds-presence-btn fds-presence-btn--p ${p.presence === 'present' ? 'fds-presence-btn--active' : ''}`}
                          onClick={() => setPresence(p, 'present')}
                          title={t('session.presence.present')}
                        >
                          <Check size={12} strokeWidth={2.5} /> {t('session.presence.present')}
                        </button>
                        <button
                          className={`fds-presence-btn fds-presence-btn--a ${p.presence === 'absent' ? 'fds-presence-btn--active' : ''}`}
                          onClick={() => setPresence(p, 'absent')}
                          title={t('session.presence.absent')}
                        >
                          <X size={12} strokeWidth={2.5} /> {t('session.presence.absent')}
                        </button>
                      </span>
                      <span className="fds-card-state">
                        {done && <span className="fds-done" title={t('session.recorded')}><Check size={12} strokeWidth={3} /></span>}
                        {p.v2_participant_id == null && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setLinking(p.id)} title={t('session.linkToEveilHelp')}>
                            <Link2 size={12} /> {t('session.linkToEveil')}
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm fds-remove" onClick={() => removeParticipant(p)} title={t('session.removeParticipant')} aria-label={t('session.removeParticipant')}>
                          <X size={12} />
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setSelectedSpId(p.id)}>
                          {t('fds.open')}
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {!adding ? (
              <button className="fds-card fds-card--add" onClick={() => setAdding(true)}>
                <UserPlus size={16} />
                <span>{t('session.addParticipant')}</span>
              </button>
            ) : (
              <div className="fds-card fds-card--addform">
                <ParticipantSearch onPick={addParticipant} onCancel={() => setAdding(false)} />
              </div>
            )}
          </div>

          {removed_participants.length > 0 && (
            <div className="fds-removed">
              <span className="mat-recap-k">{t('session.removed')}</span>
              {removed_participants.map((r) => (
                <span key={r.id} className="fds-removed-item">
                  {r.display_name}
                  {r.removed_by_name && <span className="usage-muted"> · {t('session.removedBy')} {r.removed_by_name}</span>}
                  <button className="btn btn-ghost btn-sm" onClick={() => restoreParticipant(r.id)}><RotateCcw size={11} /> {t('session.restoreParticipant')}</button>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
